import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionReplyDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { useTrackingState } from 'contexts/TrackingProvider';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import React, { useCallback, useRef } from 'react';
import { hideScrollbar } from 'styles/common';
import { useSlide } from 'ui-hooks/useSlide';
import { SectionReply } from 'app-components/reply/SectionReply';

type SectionProps = {
  section: SectionReplyDto;
  contents: Record<number, ContentRelayDto>;
  episodes: Record<number, EpisodeRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  replies: Record<number, ReplyRelayDto>;
};

const Height = 232;
const VerticalGap = 8;
const HorizontalGap = 8;

export const SectionReplies = ({
  section,
  contents,
  episodes,
  replies,
  profileLites,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionReply,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  if (section.replyIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {section.replyIds.map((replyId, index) => {
            const reply = replies[replyId];
            if (!reply || reply.isDeleted) {
              return null;
            }

            return (
              <StyledSectionReply
                key={replyId}
                onClick={() => handleOnClickTracker(reply.contentId, index)}
                reply={reply}
                contents={contents}
                episodes={episodes}
                profileLites={profileLites}
                descriptionLineCount={4}
                trackerExtraInfo={trackerExtraInfo}
              />
            );
          })}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionReplies;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      overflow-x: hidden;
      padding: 0;
      margin: 0 15px;
    }
  `,
);

const StyledSectionReply = styled(SectionReply)`
  width: 160px;
  height: ${Height}px;

  @media (${isPC}) {
    width: 169px;
  }
`;
